import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Input,
    Button,
    Select,
    SpaceBetween,
    Checkbox,
    ColumnLayout,
    Container
} from "@amzn/awsui-components-react";
import {
    List
} from "@amzn/awsui-components-console";
import appSettings from "../../CorpReporting/app_settings";
import './style.scss'
import { PROCESS_KEYS, DEF_VALUES, SELECT_TYPES } from "src/Ateam-BI/Frisco/Constants";
import { AppSettings, CustomProcessSchemaInputProps, GlobalState, SchemaRow } from "./Types/customerProcessSchemaInput";

const indexRows = (rows: SchemaRow[]) => {
    return rows.map((row, index) => ({
        [PROCESS_KEYS.SCHEMA_KEYS.column_index]: row[PROCESS_KEYS.SCHEMA_KEYS.column_index] || index + 1,
        [PROCESS_KEYS.SCHEMA_KEYS.prev_column_index]: row[PROCESS_KEYS.SCHEMA_KEYS.column_index],
        ...row,
    }));
}

const CustomProcessSchemaInput: React.FC<CustomProcessSchemaInputProps> = (props) => {
    const currentStage = useSelector((globalState: GlobalState) => globalState.auth.currentStage);
    const currentAppId = useSelector((globalState: GlobalState) => globalState.auth.currentAppId);
    const schemaRows = props.getValues(props.field)
    const [rows, setRows] = useState<SchemaRow[]>(schemaRows ? indexRows(schemaRows) : []);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isCorpReportingApp, setIsCorpReportingApp] = useState<boolean>(false);
    const [rowsCount, setRowsCount] = useState<number>(0);

    useEffect(() => {
        loadRows();

        // Set if current app is corp reporting app
        const settings = appSettings as AppSettings;
        setIsCorpReportingApp(() => currentAppId === settings[currentStage]?.appId);
        return () => {
        };
    }, []);

    useEffect(() => {
        loadRows();
    }, [props?.delsertRadioKeyVisible]);

    useEffect(() => {
        if (isInValid()) {
            let msg = '';
            if (props?.errorMessage) {
                msg = props?.errorMessage;
            }
            setErrorMessage(msg);
        } else {
            setErrorMessage('');
            props.updateValue(props.field, getMergedInputValues());
        }
    }, [rows]);
    

    const getMergedInputValues = (): SchemaRow[] => {
        const filtered = rows?.filter((row) => row != null);
        for (let i = 0; i < filtered.length; i++) {
            if (filtered[i][props.selectKey] === 'DECIMAL') {
                filtered[i][props.lengthKey] = null;
                if (!filtered[i].hasOwnProperty(props.scaleKey) || filtered[i][props.scaleKey] == null) {
                    filtered[i][props.scaleKey] = DEF_VALUES.scale;
                }
                if (!filtered[i].hasOwnProperty(props.precisionKey) || filtered[i][props.precisionKey] == null) {
                    filtered[i][props.precisionKey] = DEF_VALUES.precision;
                }
            } else if (filtered[i][props.selectKey] === 'VARCHAR') {
                filtered[i][props.scaleKey] = null;
                filtered[i][props.precisionKey] = null;
                if (!filtered[i].hasOwnProperty(props.lengthKey) || filtered[i][props.lengthKey] == null) {
                    filtered[i][props.lengthKey] = DEF_VALUES.length;
                }
            } else {
                filtered[i][props.lengthKey] = null;
                filtered[i][props.scaleKey] = null;
                filtered[i][props.precisionKey] = null;
            }
        }
        return filtered;
    };

    const isInValid = (): boolean => {
        return !!(props.validator && !props.validator(getMergedInputValues()));
    };

    const onEdit = (value: any, columnIndex: number, key: string): void => {
        const index = rows.findIndex(row => row[PROCESS_KEYS.SCHEMA_KEYS.column_index] === columnIndex);
        rows[index][key] = value;
        rows[index][props.lastUpdatedTimeKey] = null;
        setRows([...rows]);
    };

    const getInputItem = (value: SchemaRow, columnIndex: number): JSX.Element => {
        return <div className='rows' key={columnIndex}>
            <Container>
            <SpaceBetween direction="horizontal" size="xs" key={columnIndex}>
                <SpaceBetween direction="vertical" size="xs">
                    <Box variant="h4">Column name</Box>
                    <Input
                        onChange={({ detail }) => {
                            // Column names are fixed when the schema is locked to an
                            // uploaded file; ignore any edit attempts.
                            if (props?.lockColumns) {
                                return
                            }
                            let val = detail.value
                            if (props?.postProcess) {
                                val = props.postProcess(detail.value)
                            }
                            onEdit(val, columnIndex, props.inputKey)
                        }}
                        placeholder={props.placeholder}
                        value={value.hasOwnProperty(props.inputKey) ? value[props.inputKey] : ''}
                        readOnly={props?.lockColumns === true}
                        disabled={props?.lockColumns === true}
                    />
                </SpaceBetween>

                <SpaceBetween direction="vertical" size="xs">
                    <Box variant="h4">Data type</Box>
                    <Select
                        selectedOption={value[props.selectKey] ? { value: value[props.selectKey] } : null}
                        onChange={({ detail }) =>
                            onEdit(detail.selectedOption.value, columnIndex, props.selectKey)
                        }
                        options={props.getOptions(props.field, props.selectKey)}
                        placeholder={props?.selectHint ? props.selectHint : "Choose options"}
                    />
                </SpaceBetween>
                {
                    value[props.selectKey] === SELECT_TYPES.DECIMAL ?
                        <SpaceBetween direction="vertical" size="xs">
                            <Box variant="h4">Scale</Box>
                            <Input
                                onChange={({ detail }) => {
                                    const val = detail.value;
                                    if (isNaN(Number(val))) {
                                        setErrorMessage("scale can only be a number");
                                    } else if (Number(val) % 1 !== 0) {
                                        setErrorMessage("scale can only be an integer");
                                    } else {   
                                        onEdit(val, columnIndex, props.scaleKey);
                                    }
                                    
                                }}
                                placeholder="scale"
                                value={value.hasOwnProperty(props.scaleKey) && value[props.scaleKey] != null ? value[props.scaleKey] : DEF_VALUES.scale}
                            />
                        </SpaceBetween> :
                        <></>
                }
            
                {
                    value[props.selectKey] === SELECT_TYPES.DECIMAL ?
                        <SpaceBetween direction="vertical" size="xs">
                            <Box variant="h4">Precision</Box>
                            <Input
                                onChange={({ detail }) => {
                                    const val = detail.value;
                                    if (isNaN(Number(val))) {
                                        setErrorMessage("precision can only be a number");
                                    } else if (Number(val) % 1 !== 0) {
                                        setErrorMessage("precision can only be an integer");
                                    } else {   
                                        onEdit(val, columnIndex, props.precisionKey);
                                    }
                                }}
                                placeholder="precision"
                                value={value.hasOwnProperty(props.precisionKey) && value[props.precisionKey] != null ? value[props.precisionKey] : DEF_VALUES.precision}
                            />
                        </SpaceBetween> :
                        <></>
                }
                {
                    value[props.selectKey] === SELECT_TYPES.VARCHAR ?
                        <SpaceBetween direction="vertical" size="xs">
                            <Box variant="h4">Length</Box>
                            <Input
                                onChange={({ detail }) => {
                                    const val = detail.value;
                                    if (isNaN(Number(val))) {
                                        setErrorMessage("length can only be a number");
                                    } else if (Number(val) % 1 !== 0) {
                                        setErrorMessage("length can only be an integer");
                                    } else {   
                                        onEdit(val, columnIndex, props.lengthKey);
                                    }
                                }}
                                placeholder="length"
                                value={value.hasOwnProperty(props.lengthKey) && value[props.lengthKey] != null ? value[props.lengthKey] : DEF_VALUES.length}
                            />
                        </SpaceBetween> : <></>

                }
                {
                    props?.radioKeyVisible && (
                        <Checkbox
                            onChange={({ detail }) =>
                                onEdit(detail.checked, columnIndex, props.radioKey)
                            }
                            checked={value[props.radioKey]}
                        >
                            {props.radioText}
                        </Checkbox>
                    )
                }
                {
                        <SpaceBetween direction="vertical" size="xs">
                            <Box variant="h4">Equal to value (optional)</Box>
                            <Input
                                onChange={({ detail }) => {
                                    // When user enters a value, set comparison_operator, comparison_value, and comparison_type
                                    if (detail.value && detail.value.trim() !== '') {
                                        onEdit('EQUAL_TO', columnIndex, props.comparisonOperatorKey);
                                        onEdit(detail.value, columnIndex, props.comparisonValueKey);
                                        onEdit('hardcoded', columnIndex, props.comparisonTypeKey);
                                    } else {
                                        // Clear all fields when input is empty
                                        onEdit('', columnIndex, props.comparisonOperatorKey);
                                        onEdit('', columnIndex, props.comparisonValueKey);
                                        onEdit('', columnIndex, props.comparisonTypeKey);
                                    }
                                }}
                                placeholder="Enter expected value"
                                value={value.hasOwnProperty(props.comparisonValueKey) ? value[props.comparisonValueKey] : ''}
                            />
                        </SpaceBetween> 
                }

                {
                    (props?.delsertRadioKeyVisible || props?.nonEmptyColumnRadioKeyVisible || props?.encryptRadioKeyVisible) && (
                        <Box>
                            <br/>
                            <SpaceBetween direction="vertical" size="xs">
                                {props?.delsertRadioKeyVisible && (
                                    <Checkbox
                                        onChange={({ detail }) =>
                                            onEdit(detail.checked, columnIndex, props.delsertKey)
                                        }
                                        checked={value[props.delsertKey]}
                                    >
                                        {props.delsertRadioText}
                                    </Checkbox>
                                )}
                                {props?.nonEmptyColumnRadioKeyVisible && (
                                    <Checkbox
                                        onChange={({ detail }) =>
                                            onEdit(detail.checked, columnIndex, props.nonEmptyColumnKey)
                                        }
                                        checked={value[props.nonEmptyColumnKey]}
                                    >
                                        {props.nonEmptyColumnRadioText}
                                    </Checkbox>
                                )}
                                {props?.encryptRadioKeyVisible && props?.encryptKey && (
                                    <Checkbox
                                        onChange={({ detail }) =>
                                            onEdit(detail.checked, columnIndex, props.encryptKey!)
                                        }
                                        checked={value[props.encryptKey!] || false}
                                    >
                                        {props.encryptRadioText || '🔒 Encrypt'}
                                    </Checkbox>
                                )}
                            </SpaceBetween>
                        </Box>
                    )
                }

                {!props?.lockColumns && (
                    <div className="row">
                        <Button iconName="close" variant="icon" onClick={() => {
                            deleteRow(columnIndex);
                        }} />
                    </div>
                )}
                </SpaceBetween>
            </Container>
        </div>
    };

    const deleteRow = (columnIndex: number): void => {
        const index = rows.findIndex(row => row[PROCESS_KEYS.SCHEMA_KEYS.column_index] === columnIndex);
        rows.splice(index, 1);
        setRows([...rows]);
    };

    const addRow = (): void => {
        const newRow: SchemaRow = {
            [props.selectKey]: null,
            [props.inputKey]: '',
            [props.radioKey]: props.radioKeyDefault,
            [props.delsertKey]: props.delsertRadioKeyDefault,
            [props.nonEmptyColumnKey]: props.nonEmptyColumnRadioKeyDefault,
            [props.scaleKey]: DEF_VALUES.scale,
            [props.precisionKey]: DEF_VALUES.precision,
            [props.lengthKey]: DEF_VALUES.length,
            [props.lastUpdatedTimeKey]: null,
            [props.comparisonOperatorKey]: '',
            [props.comparisonValueKey]: '',
            [props.comparisonTypeKey]: '',
            ...(props.encryptKey ? { [props.encryptKey]: props.encryptRadioKeyDefault || false } : {}),
            [PROCESS_KEYS.SCHEMA_KEYS.column_index]: rowsCount + 1,
            [PROCESS_KEYS.SCHEMA_KEYS.prev_column_index]: undefined,
            [PROCESS_KEYS.SCHEMA_KEYS.prev_column_name]: undefined,
        };
        setRows([...rows, newRow]);
        setRowsCount(rowsCount + 1);
    };

    const getAddButton = (): JSX.Element => {
        // No new columns when the schema is locked to an uploaded file.
        if (props?.lockColumns) {
            return <></>;
        }
        return <div> <Button onClick={addRow}>Add</Button> </div>;
    };

    const loadRows = (): void => {
        const values = props.getValues(props.field);
        if (values == null) {
            return;
        }
        const newValues = indexRows(values);
        setRows(structuredClone(newValues));
        setRowsCount(values.length);
    };

    return (
        <ColumnLayout columns={1}>
            <SpaceBetween size="m">
                {props.info && props.info !== ' ' && props.info !== '' && (
                    <>
                        <span className="info">{props.info}</span>
                        <br />
                    </>
                )}
                {errorMessage && errorMessage !== '' && (
                    <>
                        <span className="error"> {errorMessage} </span>
                        <br />
                    </>
                )}
                <List
                    items={Array.from(rows)}
                    onSortingChange={({ detail }) => {
                        const sortedItems = detail.items.map((item, index) => {
                            return {
                                ...item,
                                [PROCESS_KEYS.SCHEMA_KEYS.column_index]: index +1,
                            }
                        })
                        setRows(sortedItems)
                    }}
                    ariaLabel="Sortable list"
                    renderItem={(row: SchemaRow) => ({ 
                        id: row[PROCESS_KEYS.SCHEMA_KEYS.column_index],
                        content: getInputItem(row, row[PROCESS_KEYS.SCHEMA_KEYS.column_index])
                    })}
                    sortable
                />
                {getAddButton()}
            </SpaceBetween>
        </ColumnLayout>
    );
};

export default CustomProcessSchemaInput;
